import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SidebarMenu } from '@/components/ui/sidebar';
import { usePinnedFeatures } from './use-pinned-features';
import { useHiddenFeatures } from './use-hidden-features';
import {
	getAllFeatureItems,
	isFeatureAvailable,
	type FeatureAuth,
	type FeatureItem,
} from './FeatureNavigator';
import { useFeatureDecorators } from './use-feature-decorators';
import { SortablePinnedItem } from './SortablePinnedItem';

export function PinnedFeatures() {
	const { pathname } = useLocation();
	const { pinnedIds, unpin, reorder } = usePinnedFeatures();
	const { hiddenIds } = useHiddenFeatures();
	const decorators = useFeatureDecorators();
	const auth = useSelector((state: { auth: FeatureAuth }) => state.auth);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 5 },
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const pinnedItems = useMemo(() => {
		const all = getAllFeatureItems();
		return pinnedIds
			.map((id) => all.find((f) => f.id === id))
			.filter(
				(f): f is FeatureItem =>
					!!f && isFeatureAvailable(f, auth) && !hiddenIds.includes(f.id),
			);
	}, [pinnedIds, hiddenIds, auth]);

	if (pinnedItems.length === 0) return null;

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			reorder(active.id as string, over.id as string);
		}
	};

	return (
		<SidebarMenu className="px-2">
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				modifiers={[restrictToVerticalAxis]}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={pinnedItems.map((item) => item.id)}
					strategy={verticalListSortingStrategy}
				>
					{pinnedItems.map((item) => (
						<SortablePinnedItem
							key={item.id}
							item={item}
							decorator={decorators[item.id]}
							isActive={pathname.startsWith(item.url)}
							onUnpin={unpin}
						/>
					))}
				</SortableContext>
			</DndContext>
		</SidebarMenu>
	);
}
